/**
 * GUARDIAN CONSENT flags (P41).
 *
 * Context: `docs/auditoria/2026-08-14-data-safety.md` uncovered that the app
 * declared it wasn't directed at children under 13 while its schema models
 * `student_profile.birth_date` and `guardian_link`. The owner decided
 * **there are minors and it gets declared**;
 * `docs/auditoria/2026-08-15-consentimiento-representante.md` is the record of this build.
 *
 * Two switches, and the difference between them matters:
 *
 * - `academy.guardian_consent` — the **record**: collecting, proving and
 *   revoking consent. Turning it on blocks no one: it creates the surface
 *   for the academy to leave a record inside the app of what it collects outside it today.
 * - `academy.guardian_consent.write_gates` — the **teeth**: without valid
 *   consent, a minor student cannot post in the classroom chat or the forum.
 *
 * **The age threshold is NOT a flag.** It's configuration
 * (`app_config` → `academy.guardian_consent_age`, org level, default 18),
 * just like `org_settings.adult_age` isn't a flag for avatars: a
 * two-position switch cannot express "in this country it's 14 and in that one it's 18".
 *
 * ## Why the teeth start OFF (and what that implies)
 *
 * The policy is fail-closed with no exception: absent or unreadable age
 * counts as a minor. Turned on today, that silences in chat and forum
 * **every student with no birth date in their record** — and the star
 * importer (`io.students`, the path an academy uses to upload its Excel
 * roster) **doesn't even have a birth-date column**. Turning it on by
 * default wouldn't protect one more minor: it would move the problem to an availability incident on deploy day.
 *
 * It's the SAME stance as `academy.entitlement.write_gates`, which can also
 * silence a student and also starts off. The decision to turn it on belongs
 * to the owner and is laid out with its implications in the record.
 */

import type { FeatureManifest } from "../country/packs";

const GUARDIAN_CONSENT: FeatureManifest = {
  key: "academy.guardian_consent",
  name: "Consentimiento del representante",
  description:
    "Registro del consentimiento del representante de un alumno menor: para qué se autoriza (cuenta, chat del aula, foro, IA, foto), quién lo autorizó, cuándo, cómo se probó y su revocación. Incluye la solicitud por correo con doble confirmación, para que la familia pueda autorizar sin pasar por la academia. Apagada, la academia sigue recabándolo fuera de la app.",
  scope: "org",
  status: "beta",
  gateable: true,
  // Starts OFF: it's a new surface for minors' personal data, and turning
  // it on is a decision for the academy, not a factory default.
  defaultEnabled: false,
  since: "2026-08",
  docsSlug: "gente",
  implications: {
    navHidden: [],
    flowsBlocked: [
      "Registrar el consentimiento del representante de un alumno",
      "Pedir el consentimiento por correo con doble confirmación",
      "Revocar un consentimiento ya concedido",
    ],
    dataPolicy: "preserved",
  },
};

const GUARDIAN_CONSENT_WRITE_GATES: FeatureManifest = {
  key: "academy.guardian_consent.write_gates",
  name: "Exigir el consentimiento para publicar",
  description:
    "Sin consentimiento vigente, un alumno por debajo de la edad que declare la academia no publica en el chat del aula ni en el foro. Fail-closed: si no consta su fecha de nacimiento, o es ilegible, cuenta como menor. Lo ya publicado no se toca ni se borra.",
  scope: "org",
  status: "beta",
  gateable: true,
  // Starts OFF — see the module header: turned on with no birth dates in
  // the records, it silences the entire student body.
  defaultEnabled: false,
  since: "2026-08",
  docsSlug: "gente",
  requires: ["academy.guardian_consent"],
  implications: {
    navHidden: [],
    flowsBlocked: [
      "Impedir al alumno menor sin consentimiento publicar en el chat del aula",
      "Impedir al alumno menor sin consentimiento abrir hilos y responder en el foro",
    ],
    dataPolicy: "preserved",
  },
};

/** Manifests of guardian consent (source `guardian_consent`). */
export function guardianConsentManifests(): FeatureManifest[] {
  return [GUARDIAN_CONSENT, GUARDIAN_CONSENT_WRITE_GATES];
}

/** Greppable keys from the client (mirror of `platform::features::keys`). */
export const FLAG_GUARDIAN_CONSENT = GUARDIAN_CONSENT.key;
export const FLAG_GUARDIAN_CONSENT_WRITE_GATES = GUARDIAN_CONSENT_WRITE_GATES.key;
